import { useState, useEffect } from 'react';
import { collection, doc, getDoc, setDoc, serverTimestamp, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { SubscriptionTier, UserSubscription, SubscriptionPrices } from '../../models/types';
import { toast } from 'react-toastify';
import { Check } from 'lucide-react';

const BusinessPlan = () => {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionTier | null>(null);
  const [currentSubscription, setCurrentSubscription] = useState<UserSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prices, setPrices] = useState<SubscriptionPrices>({
    normal: { regular: 5 },
    gold: { regular: 20 },
    platinum: { regular: 50 }
  });

  useEffect(() => {
    if (!currentUser) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch subscription
        const subscriptionDoc = await getDoc(doc(db, 'subscriptions', currentUser.uid));
        if (subscriptionDoc.exists()) {
          const data = subscriptionDoc.data() as UserSubscription;
          setCurrentSubscription({
            ...data,
            startDate: data.startDate.toDate(),
            endDate: data.endDate.toDate()
          });
          setSelectedPlan(data.tier);
        }

        // Fetch prices
        const pricesDoc = await getDocs(collection(db, 'systemParams'));
        const pricesData = pricesDoc.docs.find(doc => doc.id === 'subscriptionPrices');
        if (pricesData) {
          setPrices(pricesData.data() as SubscriptionPrices);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Error loading subscription data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  const handleSelectPlan = async (plan: SubscriptionTier) => {
    if (!currentUser) return;

    try {
      setSaving(true);
      
      const now = new Date();
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 1); // 1 month subscription

      const subscriptionData: UserSubscription = {
        userId: currentUser.uid,
        tier: plan,
        startDate: now,
        endDate: endDate,
        active: true
      };

      await setDoc(doc(db, 'subscriptions', currentUser.uid), {
        ...subscriptionData,
        startDate: serverTimestamp(),
        endDate: endDate
      });
      
      setCurrentSubscription(subscriptionData);
      setSelectedPlan(plan);
      toast.success('Subscription updated successfully');
    } catch (error) {
      console.error('Error updating subscription:', error);
      toast.error('Error updating subscription');
    } finally {
      setSaving(false);
    }
  };

  const hasSpecialOffer = (planPrices: { regular: number; promotional?: number }) => {
    return planPrices.promotional === 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto container-padding section-padding">
      <h1 className="heading-lg text-center mb-4">{t('businessPlan.title')}</h1>
      <p className="text-body text-center text-gray-600 max-w-2xl mx-auto mb-8 sm:mb-12">
        {t('businessPlan.subtitle')}
      </p>

      <div className="grid md:grid-cols-4 gap-6 lg:gap-8 max-w-6xl mx-auto">
        {/* Free Plan */}
        <div className={`bg-white rounded-lg shadow-lg overflow-hidden transition-transform hover:scale-105 ${
          selectedPlan === 'free' ? 'ring-2 ring-primary-600' : ''
        }`}>
          <div className="bg-gray-100 py-3 sm:py-4 text-center">
            <h3 className="heading-xs">{t('businessPlan.free')}</h3>
          </div>
          <div className="card-padding">
            <div className="text-center mb-4 sm:mb-6">
              <span className="heading-md">€0</span>
              <span className="text-body-sm text-gray-600">{t('businessPlan.month')}</span>
            </div>
            <ul className="spacing-sm mb-4 sm:mb-6">
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 shrink-0 mt-0.5" />
                <span className="ml-2 text-body-sm">Access to all projects</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 shrink-0 mt-0.5" />
                <span className="ml-2 text-body-sm">Basic profile visibility</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 shrink-0 mt-0.5" />
                <span className="ml-2 text-body-sm">Standard support</span>
              </li>
            </ul>
            <button
              onClick={() => handleSelectPlan('free')}
              disabled={saving}
              className={`w-full py-2 sm:py-3 rounded-lg text-button transition-colors ${
                selectedPlan === 'free'
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'border border-primary-600 text-primary-600 hover:bg-primary-50'
              }`}
            >
              {selectedPlan === 'free' ? t('businessPlan.currentPlan') : t('businessPlan.selectPlan')}
            </button>
          </div>
        </div>

        {/* Normal Plan */}
        <div className={`bg-white rounded-lg shadow-lg overflow-hidden transition-transform hover:scale-105 relative ${
          selectedPlan === 'normal' ? 'ring-2 ring-primary-600' : ''
        }`}>
          {hasSpecialOffer(prices.normal) && (
            <div className="absolute top-3 sm:top-4 right-3 sm:right-4 z-10">
              <div className="bg-red-500 text-white px-2 sm:px-3 py-1 text-xs sm:text-sm font-bold transform rotate-12 shadow-lg">
                {t('businessPlan.specialOffer')}
              </div>
            </div>
          )}
          <div className="bg-gray-100 py-3 sm:py-4 text-center">
            <h3 className="heading-xs">{t('businessPlan.normal')}</h3>
          </div>
          <div className="card-padding">
            <div className="text-center mb-4 sm:mb-6">
              <span className="heading-md">€{prices.normal.promotional !== undefined ? prices.normal.promotional : prices.normal.regular}</span>
              <span className="text-body-sm text-gray-600">{t('businessPlan.month')}</span>
              {prices.normal.promotional !== undefined && prices.normal.promotional !== prices.normal.regular && (
                <div className="text-caption line-through">€{prices.normal.regular}/month</div>
              )}
            </div>
            <ul className="spacing-sm mb-4 sm:mb-6">
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 shrink-0 mt-0.5" />
                <span className="ml-2 text-body-sm">{t('businessPlan.features.normal.projects')}</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 shrink-0 mt-0.5" />
                <span className="ml-2 text-body-sm">{t('businessPlan.features.normal.country')}</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 shrink-0 mt-0.5" />
                <span className="ml-2 text-body-sm">{t('businessPlan.features.normal.visibility')}</span>
              </li>
            </ul>
            <button
              onClick={() => handleSelectPlan('normal')}
              disabled={saving}
              className={`w-full py-2 sm:py-3 rounded-lg text-button transition-colors ${
                selectedPlan === 'normal'
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'border border-primary-600 text-primary-600 hover:bg-primary-50'
              }`}
            >
              {selectedPlan === 'normal' ? t('businessPlan.currentPlan') : t('businessPlan.selectPlan')}
            </button>
          </div>
        </div>

        {/* Gold Plan */}
        <div className={`bg-white rounded-lg shadow-lg overflow-hidden transition-transform hover:scale-105 border-2 border-primary-600 relative ${
          selectedPlan === 'gold' ? 'ring-2 ring-primary-600' : ''
        }`}>
          {hasSpecialOffer(prices.gold) && (
            <div className="absolute top-3 sm:top-4 right-3 sm:right-4 z-10">
              <div className="bg-red-500 text-white px-2 sm:px-3 py-1 text-xs sm:text-sm font-bold transform rotate-12 shadow-lg">
                {t('businessPlan.specialOffer')}
              </div>
            </div>
          )}
          <div className="bg-primary-600 py-3 sm:py-4 text-center text-white">
            <h3 className="heading-xs">{t('businessPlan.gold')}</h3>
          </div>
          <div className="card-padding">
            <div className="text-center mb-4 sm:mb-6">
              <span className="heading-md">€{prices.gold.promotional !== undefined ? prices.gold.promotional : prices.gold.regular}</span>
              <span className="text-body-sm text-gray-600">{t('businessPlan.month')}</span>
              {prices.gold.promotional !== undefined && prices.gold.promotional !== prices.gold.regular && (
                <div className="text-caption line-through">€{prices.gold.regular}/month</div>
              )}
            </div>
            <ul className="spacing-sm mb-4 sm:mb-6">
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 shrink-0 mt-0.5" />
                <span className="ml-2 text-body-sm">{t('businessPlan.features.gold.projects')}</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 shrink-0 mt-0.5" />
                <span className="ml-2 text-body-sm">{t('businessPlan.features.gold.countries')}</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 shrink-0 mt-0.5" />
                <span className="ml-2 text-body-sm">{t('businessPlan.features.gold.visibility')}</span>
              </li>
            </ul>
            <button
              onClick={() => handleSelectPlan('gold')}
              disabled={saving}
              className={`w-full py-2 sm:py-3 rounded-lg text-button transition-colors ${
                selectedPlan === 'gold'
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'border border-primary-600 text-primary-600 hover:bg-primary-50'
              }`}
            >
              {selectedPlan === 'gold' ? t('businessPlan.currentPlan') : t('businessPlan.selectPlan')}
            </button>
          </div>
        </div>

        {/* Platinum Plan */}
        <div className={`bg-white rounded-lg shadow-lg overflow-hidden transition-transform hover:scale-105 relative ${
          selectedPlan === 'platinum' ? 'ring-2 ring-primary-600' : ''
        }`}>
          {hasSpecialOffer(prices.platinum) && (
            <div className="absolute top-3 sm:top-4 right-3 sm:right-4 z-10">
              <div className="bg-red-500 text-white px-2 sm:px-3 py-1 text-xs sm:text-sm font-bold transform rotate-12 shadow-lg">
                {t('businessPlan.specialOffer')}
              </div>
            </div>
          )}
          <div className="bg-gray-800 py-3 sm:py-4 text-center text-white">
            <h3 className="heading-xs">{t('businessPlan.platinum')}</h3>
          </div>
          <div className="card-padding">
            <div className="text-center mb-4 sm:mb-6">
              <span className="heading-md">€{prices.platinum.promotional !== undefined ? prices.platinum.promotional : prices.platinum.regular}</span>
              <span className="text-body-sm text-gray-600">{t('businessPlan.month')}</span>
              {prices.platinum.promotional !== undefined && prices.platinum.promotional !== prices.platinum.regular && (
                <div className="text-caption line-through">€{prices.platinum.regular}/month</div>
              )}
            </div>
            <ul className="spacing-sm mb-4 sm:mb-6">
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 shrink-0 mt-0.5" />
                <span className="ml-2 text-body-sm">{t('businessPlan.features.platinum.projects')}</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 shrink-0 mt-0.5" />
                <span className="ml-2 text-body-sm">{t('businessPlan.features.platinum.countries')}</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 shrink-0 mt-0.5" />
                <span className="ml-2 text-body-sm">{t('businessPlan.features.platinum.support')}</span>
              </li>
            </ul>
            <button
              onClick={() => handleSelectPlan('platinum')}
              disabled={saving}
              className={`w-full py-2 sm:py-3 rounded-lg text-button transition-colors ${
                selectedPlan === 'platinum'
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'border border-primary-600 text-primary-600 hover:bg-primary-50'
              }`}
            >
              {selectedPlan === 'platinum' ? t('businessPlan.currentPlan') : t('businessPlan.selectPlan')}
            </button>
          </div>
        </div>
      </div>

      {currentSubscription && (
        <div className="mt-6 sm:mt-8 text-center text-caption">
          {t('businessPlan.renewsOn')} {currentSubscription.endDate.toLocaleDateString()}
        </div>
      )}
    </div>
  );
};

export default BusinessPlan;